import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  LinearProgress,
  Stack,
  Table,
  TableContainer,
  TableHead,
  Typography,
  Box,
} from '@mui/material';
import { useRouter } from 'next/router';
import {
  CompactTableRow,
  PaddedTableCell,
  PamActionButton,
} from '../components/shared/ui-extension';
import { camelCaseReplace, nameFormat } from '../helpers/stringHelpers';
import { LabeledInput } from '../components/shared/labeled-input';
import { useNotificationContext } from '../components/shared/notification';
import { useEffect, useState } from 'react';

const ConfirmDialog = (props) => {
  const {
    variant = 'text',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    showCancel = false,
    content = '',
    message = '',
    onConfirm = () => {},
    onClose = () => {},
    restrictOffClick = false,
    allowBackdropClose = false, // When true, a backdrop (outside) click closes the dialog
    backdropWarningMessage = '', // When set, a blocked backdrop click warns the user instead of doing nothing
    title = '',
    open = false,
    sx = null,
    cancelSx = null,
    color = 'error',
    bold = true,
    rows = [],
    previousRows = [],
    routerPushPageTitle = '',
    routerPushPageEndpoint = '',
    datePicker = false,
    setDates = () => {},
    dates = { startDate: '', endDate: '' },
    onRowClick = null,
    fullWidth = false,
    loading = false,
    loadingBar = false,
    loadingMessages = [
      'Updating...',
      'Processing...',
      'Almost there...',
      'Completing...',
    ],
    additionalComponents = null, // () => {},
  } = props;

  const { showMessage } = useNotificationContext();
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0);
  const [startInterval, setStartInterval] = useState(false);

  useEffect(() => {
    if (startInterval) {
      const interval = beginTimer();
      return () => clearInterval(interval);
    }
  }, [startInterval]);

  useEffect(() => {
    if (!loading) {
      setProgress(0);
      setStage(null);
      setStartInterval(false);
    }
  }, [loading]);

  const beginTimer = () => {
    const totalStages = loadingMessages.length;
    const intervalTime = 5500; // simulate each stage of lengthy loading process adjust as needed
    setStage(0);
    setProgress(0);
    const interval = setInterval(() => {
      setStage((prev) => {
        const nextStage = prev + 1;
        setProgress((nextStage / totalStages) * 100);
        if (nextStage >= totalStages) {
          clearInterval(interval);
        }
        return nextStage;
      });
    }, intervalTime);
    return interval;
  };

  const router = useRouter();

  const RowTable = (props) => {
    const { rows: selectedRows = [], selected = false } = props;
    return (
      <TableContainer
        sx={{
          maxHeight: 200,
          border: '1px solid #e0e0e0',
          borderRadius: '5px',
          mt: 2,
          overflow: 'auto',
        }}
      >
        <Table size='small'>
          <TableHead>
            <CompactTableRow>
              {Object.keys(selectedRows?.[0] || {})?.map((key, index) => (
                <PaddedTableCell key={index}>
                  {camelCaseReplace(key)}
                </PaddedTableCell>
              ))}
            </CompactTableRow>
            {selectedRows?.map((row, index) => (
              <CompactTableRow
                key={index}
                {...(selected && {
                  // comparison table is all selected rows
                  selected: true,
                  sx: {
                    backgroundColor: selected && 'rgba(255, 235, 59, 0.2)',
                  },
                })}
              >
                {Object.keys(row)?.map((key, index) => {
                  return (
                    <PaddedTableCell
                      sx={onRowClick ? { cursor: 'pointer' } : {}}
                      onClick={
                        onRowClick
                          ? (e) => {
                              onRowClick(e.target.innerHTML);
                            }
                          : () => {}
                      }
                      key={index}
                    >
                      {typeof row[key] === 'object'
                        ? row[key]?.value === true
                          ? 'True'
                          : row[key]?.value === false
                          ? 'False'
                          : row[key]?.value
                        : row[key]}
                    </PaddedTableCell>
                  );
                })}
              </CompactTableRow>
            ))}
          </TableHead>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog
      open={open}
      fullWidth={fullWidth}
      maxWidth='sm'
      onClose={(_event, reason) => {
        // Only close on a backdrop (outside) click when the caller opts in via allowBackdropClose.
        if (reason === 'backdropClick' && !allowBackdropClose) {
          // Warn (instead of silently ignoring) so users know to use the buttons.
          if (backdropWarningMessage) {
            showMessage({
              message: backdropWarningMessage,
              severity: 'warning',
              duration: 5000,
            });
          }
          return;
        }
        if (!restrictOffClick) {
          onClose();
        }
      }}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      TransitionComponent={Grow}
    >
      <DialogTitle id='alert-dialog-title'>{title}</DialogTitle>
      <Divider />
      <DialogContent>
        <DialogContentText id='alert-dialog-description'>
          {content}
        </DialogContentText>
        {message ? (
          <DialogContentText
            sx={bold ? { mt: 2, fontWeight: 'bold' } : { mt: 2 }}
            color={color}
            id='alert-dialog-description'
          >
            {message}
          </DialogContentText>
        ) : null}
        {rows?.length || previousRows?.length ? (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {previousRows?.length ? (
              <RowTable rows={previousRows} selected={true} />
            ) : null}
            <RowTable rows={rows} />
          </Stack>
        ) : null}

        {datePicker ? (
          <Stack spacing={1}>
            <LabeledInput
              type='date'
              label='Start Date'
              value={dates.startDate}
              disableFuture={true}
              onChange={(value) => {
                if (!value) return;
                setDates({ ...dates, startDate: new Date(value) });
              }}
            />
            <LabeledInput
              type='date'
              label='End Date'
              value={dates.endDate}
              onChange={(value) => {
                if (!value) return;
                setDates({ ...dates, endDate: new Date(value) });
              }}
            />
          </Stack>
        ) : null}
        {additionalComponents && typeof additionalComponents === 'function'
          ? additionalComponents()
          : null}
      </DialogContent>

      {loadingBar && loading && (
        <Box
          sx={{
            width: '100%',
            maxWidth: 500,
            mx: 'auto',
            my: 1,
            textAlign: 'center',
          }}
        >
          <Typography variant='h6' gutterBottom>
            {loadingMessages[stage] || 'Completing!'}
          </Typography>
          <LinearProgress variant='determinate' value={progress} />
        </Box>
      )}
      <DialogActions>
        {showCancel && (
          <PamActionButton
            loading={loading}
            variant={variant}
            {...(cancelSx && { sx: cancelSx })}
            buttonType='error'
            text={cancelText}
            onClick={() => onClose()}
          ></PamActionButton>
        )}

        {routerPushPageEndpoint && (
          <PamActionButton
            loading={loading}
            variant={variant}
            {...(sx && { sx })}
            text={` Go to ${routerPushPageTitle}`}
            onClick={() => {
              router.push(routerPushPageEndpoint);
              onClose();
            }}
          ></PamActionButton>
        )}

        <PamActionButton
          loading={loading}
          {...(sx && { sx })}
          variant={variant}
          onClick={() => {
            beginTimer();
            onConfirm();
          }}
          text={confirmText}
          autoFocus
        ></PamActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
